/**
 * One frame of a card growing into its opened window (Part 10 Rule 3), at
 * progress `p` (0 = the card, 1 = the window): the window scales uniformly
 * from the card's width and its clip unrolls downward from the card's
 * height, with the corners kept visually at the card radius. For non-media
 * types the card's snapshot fades out while the live content fades in.
 */
import type { FocusWindowBox } from "@/lib/focus-window-layout";

/** A wall card in the coordinates of the window's surface. */
export interface CardGrowRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface CardGrowFrame {
  /** The window body's transform, origin at its top left. */
  transform: string;
  /** The window body's clip. */
  clipPath: string;
  /** Height of the shadow plate, in window pixels. */
  visibleHeight: number;
  /** Corner radius in window pixels: on screen, the card's turning into
   *  the window's. */
  radius: number;
  /** Scale that draws the card-sized snapshot across the window. */
  snapshotScale: number;
  snapshotOpacity: number;
  liveOpacity: number;
}

/** The card's corner radius (px): `--appkit-radius-surface`, behind
 *  `rounded-card`. */
const CARD_RADIUS = 20;
/** The opened window's corner radius (px): `--appkit-radius-window`, a
 *  macOS window's (frame-window.css). The grow turns one into the other. */
const WINDOW_RADIUS = 16.5;

function clamp01(value: number): number {
  return Math.min(1, Math.max(0, value));
}

export function cardGrowFrame(
  progress: number,
  card: CardGrowRect,
  box: FocusWindowBox,
): CardGrowFrame {
  const p = clamp01(progress);
  const startScale = card.width / box.width;
  const scale = startScale + (1 - startScale) * p;
  const startHeight = Math.min(box.height, card.height / startScale);
  const visibleHeight = startHeight + (box.height - startHeight) * p;
  const radius = (CARD_RADIUS + (WINDOW_RADIUS - CARD_RADIUS) * p) / scale;
  const tx = (card.x - box.x) * (1 - p);
  const ty = (card.y - box.y) * (1 - p);
  return {
    transform: `translate(${tx}px, ${ty}px) scale(${scale})`,
    clipPath: `inset(0px 0px ${box.height - visibleHeight}px 0px round ${radius}px)`,
    visibleHeight,
    radius,
    snapshotScale: 1 / startScale,
    snapshotOpacity: clamp01(1 - (p - 0.15) / 0.4),
    liveOpacity: clamp01((p - 0.1) / 0.4),
  };
}
